import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo } from "react";
import { AppRouter } from "@/app/router/AppRouter";
import { useInitializePublicAppStore } from "@/store";
import { PublicAppStoreContext } from "@/store/utils/context";
import { GlobalToastContainer } from "@/components/toast";
import { MdsAppStyleWrapper, MdsGlobalProvider } from "@/components/styling";
import { ThemeProvider } from "@emotion/react";
import { ThemeContextProvider, useTheme } from "@/mds";
import { AppVersionHandling } from "@/app/core/AppVersionHandling";
import { PageDragAndDropProvider } from "@/components/drag-and-drop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DesktopMenuOverlay } from "@/components/desktop/desktop-menu-overlay";
import { localStorageModule } from "@/modules/local-storage";
import { electronModule } from "@/modules/electron";

const ThemedApp: FC = observer(() => {
  const { theme, themeType } = useTheme();

  // Send theme changes to electron API
  useEffect(() => {
    electronModule.setTheme({ theme: themeType });
  }, [themeType]);

  return (
    <ThemeProvider theme={theme}>
      <AppRouter />
      <DesktopMenuOverlay />
      <GlobalToastContainer />
      <AppVersionHandling />
    </ThemeProvider>
  );
});

export const AppCore: FC = observer(() => {
  const queryClient = useMemo(() => new QueryClient(), []);

  const state = useInitializePublicAppStore();

  if (state.loading) {
    /**
     * Loading.
     * @todo - Introduce some form of loading-interstitial here.
     */
    return <div />;
  }

  if (state.error || !state.data) {
    return <div>Something went wrong.</div>;
  }

  return (
    <PublicAppStoreContext.Provider value={state.data}>
      <QueryClientProvider client={queryClient}>
        <MdsGlobalProvider>
          <PageDragAndDropProvider>
            <MdsAppStyleWrapper>
              <ThemeContextProvider
                getPersistedTheme={() => localStorageModule.readThemeType()}
                persistTheme={theme => localStorageModule.writeThemeType(theme)}
              >
                <ThemedApp />
              </ThemeContextProvider>
            </MdsAppStyleWrapper>
          </PageDragAndDropProvider>
        </MdsGlobalProvider>
      </QueryClientProvider>
    </PublicAppStoreContext.Provider>
  );
});
